import { makeAutoObservable, reaction } from "mobx";
import { RootStore } from "../RootStore";
import { LeagueLevel } from "../../utils/typeValues";
import { FrontDeskApi } from "../../apis/frontDesk.api";
import { MatchResponsePayload, NationalLegues } from "../../apis/types";

export class NationalLeagueStore {
  rootStore: RootStore;
  modalData?: {
    visible: boolean;
    title: string;
    children: string;
    btnText?: string;
    btnVariant?: string;
    onClose?: () => void;
    onAccept?: () => void;
    headerIcon?: React.ReactNode;
  };
  startDate?: string = undefined;
  endDate?: string = undefined;
  isLoading: boolean = false;
  pageNum: number = 1;
  isStopPagination: boolean = false;
  leagues: NationalLegues[] = [];
  isCreateLeagueSheetVisible: boolean = false;
  leagueLevel: LeagueLevel = LeagueLevel.Low;
  isLeagueCreationInProgress: boolean = false;
  leagueId?: number = undefined;
  isLeagueMatchesLoading: boolean = false;
  leagueMatches: MatchResponsePayload[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });

    reaction(
      () => this.isCreateLeagueSheetVisible,
      (visible) => {
        if (!visible) {
          this.clearCreateLeagueSheetData();
        }
      }
    );

    reaction(
      () => this.leagueId,
      (id) => {
        if (id) {
          this.fetchLeagueMatches();
        } else {
          this.setLeagueMatches([]);
        }
      }
    );
  }
  setStartDate = (date?: string) => {
    this.startDate = date;
  };

  setLeagues = (leagues: NationalLegues[]) => {
    this.leagues = leagues;
  };

  setEndDate = (date?: string) => {
    this.endDate = date;
  };

  setLeagueLevel = (level: LeagueLevel) => {
    this.leagueLevel = level;
  };

  setLeagueId = (id?: number) => {
    this.leagueId = id;
  };

  setLeagueMatches = (matches: MatchResponsePayload[]) => {
    this.leagueMatches = matches;
  };

  clearCreateLeagueSheetData = () => {
    this.setStartDate(undefined);
    this.setEndDate(undefined);
    this.setLeagueLevel(LeagueLevel.Low);
    this.isLeagueCreationInProgress = false;
  };

  createNationalLeague = async () => {
    this.isLeagueCreationInProgress = true;
    try {
      const payload = {
        leagueStartDate: `${this.startDate}T00:00:00`,
        leagueEndDate: `${this.endDate}T00:00:00`,
        level: this.leagueLevel,
      };
      await FrontDeskApi.createNationalLeague(payload);
      this.fetchLeagues();
      this.isLeagueCreationInProgress = false;
    } catch (error: any) {
      this.isLeagueCreationInProgress = false;
      this.modalData = {
        visible: true,
        title: "Error",
        children: error.message,
        btnText: "OK",
        onAccept: () => {
          this.modalData = undefined;
        },
      };
    }
  };

  fetchLeagues = async () => {
    this.isLoading = true;
    try {
      const response = await FrontDeskApi.getAllLeagues();
      this.setLeagues(response);
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
    }
  };

  fetchLeagueMatches = async () => {
    this.isLeagueMatchesLoading = true;
    try {
      const response = await FrontDeskApi.getLeagueMatches(this.leagueId!);
      this.setLeagueMatches(response);
      this.isLeagueMatchesLoading = false;
    } catch (error: any) {
      this.isLeagueMatchesLoading = false;
      this.modalData = {
        visible: true,
        title: "Error",
        children: error.message,
        btnText: "OK",
        onAccept: () => {
          this.modalData = undefined;
        },
      };
    }
  };

  reset = () => {
    this.modalData = undefined;
    this.isLoading = false;
    this.startDate = undefined;
    this.endDate = undefined;
    this.pageNum = 1;
    this.isStopPagination = false;
    this.leagues = [];
    this.isCreateLeagueSheetVisible = false;
    this.isLeagueCreationInProgress = false;
    this.leagueId = undefined;
    this.isLeagueMatchesLoading = false;
    this.leagueMatches = [];
  };
}
